"use client";

import type SettingsModal from "@/components/SettingsModal";
import type { ImpromptuExperience } from "@/components/impromptu/ImpromptuExperience";
import { loadImpromptu, loadSettings } from "@/lib/secondary-feature-loaders";
import * as Dialog from "@radix-ui/react-dialog";
import { type ComponentProps, type ComponentType, useEffect, useState } from "react";

function usePreparedComponent<P extends object>(
  load: () => Promise<ComponentType<P>>,
  peek: () => ComponentType<P> | undefined
) {
  const [Component, setComponent] = useState<ComponentType<P> | null>(() => peek() || null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setError(false);
    void load().then(
      (component) => {
        if (live) setComponent(() => component);
      },
      () => {
        if (live) setError(true);
      }
    );
    return () => {
      live = false;
    };
  }, [load, attempt]);
  return { Component, error, retry: () => setAttempt((value) => value + 1) };
}

function Preparation({
  error,
  retry,
  close,
}: {
  error: boolean;
  retry: () => void;
  close?: () => void;
}) {
  return (
    <div role="status" className="rounded-lg border border-white/15 bg-[#111218] p-6 text-white">
      <p>{error ? "Unable to load this feature." : "Loading…"}</p>
      {error && (
        <button type="button" className="mt-3 underline" onClick={retry}>
          Try again
        </button>
      )}
      {close && (
        <button type="button" className="ml-4 mt-3 underline" onClick={close}>
          Close
        </button>
      )}
    </div>
  );
}

const settingsComponent = () => loadSettings().then((module) => module.default);
const impromptuComponent = () => loadImpromptu().then((module) => module.ImpromptuExperience);
const preparedSettings = () => loadSettings.peek()?.default;
const preparedImpromptu = () => loadImpromptu.peek()?.ImpromptuExperience;

/** Mounted on first open only, then retained so drafts survive closing. */
export function DeferredSettings(props: ComponentProps<typeof SettingsModal>) {
  const { Component, error, retry } = usePreparedComponent(settingsComponent, preparedSettings);
  if (Component) return <Component {...props} />;
  if (!props.isOpen) return null;
  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[2147483646] bg-black/75" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[2147483647] -translate-x-1/2 -translate-y-1/2"
        >
          <Dialog.Title className="sr-only">Settings</Dialog.Title>
          <Preparation error={error} retry={retry} close={props.onClose} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DeferredImpromptu(props: ComponentProps<typeof ImpromptuExperience>) {
  const { Component, error, retry } = usePreparedComponent(impromptuComponent, preparedImpromptu);
  return Component ? (
    <Component {...props} />
  ) : (
    <Preparation error={error} retry={retry} close={props.onBack} />
  );
}
